import { memo, useCallback, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { BlockUi } from "../components/block-ui/BlockUI";
import { CommandParameterDialog } from "../components/dialogs/ui/CommandParameterDialog";
import { CounterDataDialog } from "../components/dialogs/ui/CounterData";
import { CreateChoiceListDialog } from "../components/dialogs/ui/CreateChoiceListDialog";
import { CreateDialogModal } from "../components/dialogs/ui/CreateDialog";
import { DiagnosticDialogModal } from "../components/dialogs/ui/DiagnosticDialog";
import { FindDialogModal } from "../components/dialogs/ui/FindDialog";
import { MassKDLDialog } from "../components/dialogs/ui/MassKdl";
import { PowerProfileDialog } from "../components/dialogs/ui/PowerProfile";
import { ReloginForm } from "../components/dialogs/ui/ReloginForm";
import useContextMenu from "../components/tree/hooks/useContextMenu";
import { getContextMenuProps } from "../components/tree/utils/getContextMenuProps";
import { useModalContext } from "../context/ModalContext";
import { useRpc } from "../context/RpcContext";
import { useToast } from "../context/toast/ui";
import { ResursTreeApi } from "../services/modules/tree";
import { RpcClient } from "../services/rpc-socket/rpcClient";
import {
  treeDelNodeBatch,
  treeInsNodeBatch,
  treeUpdNodeBatch,
} from "../services/slices/tree";
import { RootState, useAppDispatch } from "../services/types/store-types";
import { useAuthContext } from "./AuthContext";
import { usePlannerReady } from "../services/hooks/usePlannerReady";

const SubscribeContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { subscribe, wsOpened } = useRpc();
  const { showMessage } = useToast();
  const { isAuthenticated, setUserCaption } = useAuthContext();

  const { modalId, openModal, closeModal, modalData, showModal, setModalData } =
    useModalContext();

  const dispatch = useAppDispatch();

  const { updatePlannerReady } = usePlannerReady();

  const properties = useSelector((state: RootState) => state.tree.currentProps);
  const currentPropsTree = useSelector(
    (state: RootState) => state.tree.currentPropsTree,
  );

  const { setMenuItems } = useContextMenu();
  const lastSelect = useSelector(
    (state: RootState) => state.tree.selectedNodes,
  );

  const operatorTree = useSelector((state: RootState) => state.tree.operator);
  const location = useLocation().pathname.split("/")[1];

  useEffect(() => {
    if (!properties) {
      setMenuItems([]);
      return;
    }

    // Проверяем, что properties принадлежат текущему выбранному дереву
    // Это предотвращает показ меню со старыми командами при смене дерева
    if (currentPropsTree !== lastSelect.tree) {
      // Очищаем меню пока ждём правильные properties
      setMenuItems([]);
      return;
    }

    setMenuItems(
      getContextMenuProps(
        properties,
        lastSelect,
        (modalData) => openModal(modalData, "command-parameter"),
        location,
      ),
    );
  }, [
    lastSelect,
    location,
    openModal,
    properties,
    currentPropsTree,
    setMenuItems,
  ]);

  const bufferRef = useRef<{
    ins: ResursTreeApi.TTreeUpdateResponseIns[];
    upd: ResursTreeApi.TTreeUpdateResponseUpd[];
    del: ResursTreeApi.TTreeUpdateResponseDel[];
  }>({
    ins: [],
    upd: [],
    del: [],
  });

  const flush = useCallback(() => {
    const { ins, upd, del } = bufferRef.current;

    if (ins.length > 0) {
      dispatch(treeInsNodeBatch(ins));
      bufferRef.current.ins = [];
    }

    if (upd.length > 0) {
      dispatch(treeUpdNodeBatch(upd));
      bufferRef.current.upd = [];
    }

    if (del.length > 0) {
      dispatch(treeDelNodeBatch(del));
      bufferRef.current.del = [];
    }
  }, [dispatch]);

  const flushIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const flushTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const stopFlushInterval = useCallback(() => {
    if (flushIntervalRef.current) {
      clearInterval(flushIntervalRef.current);
      flushIntervalRef.current = null;
    }
    if (flushTimeoutRef.current) {
      clearTimeout(flushTimeoutRef.current);
      flushTimeoutRef.current = null;
    }
  }, []);

  const handleTreeUpd = useCallback(
    (params: any) => {
      if ("ins" in params) {
        bufferRef.current.ins.push(
          params as ResursTreeApi.TTreeUpdateResponseIns,
        );
      }
      if ("upd" in params) {
        const paramsUpd = params as ResursTreeApi.TTreeUpdateResponseUpd;
        if (paramsUpd.tree === "operator") {
          const targetOperator = operatorTree.find((item) => item.state === 8);

          const newOperData = paramsUpd.upd.find(
            (item) => item.id === targetOperator?.id,
          );

          if (!newOperData) return;
          setUserCaption(newOperData.text);
        }
        bufferRef.current.upd.push(paramsUpd);
      }
      if ("del" in params) {
        bufferRef.current.del.push(params);
      }

      if (!flushIntervalRef.current) {
        flushIntervalRef.current = setInterval(() => {
          flush();
        }, 200);
      }

      // Сбрасываем таймаут остановки интервала
      if (flushTimeoutRef.current) {
        clearTimeout(flushTimeoutRef.current);
      }

      flushTimeoutRef.current = setTimeout(() => {
        stopFlushInterval();
      }, 1000);
    },
    [flush, stopFlushInterval, operatorTree, setUserCaption],
  );

  const handleNavRedirect = (params: { url: string }) => {
    if (params.url) {
      window.location.href = "#" + params.url;
    }
  };

  useEffect(() => {
    (async () => {
      if (!isAuthenticated) return;
      await RpcClient.call("subscribe", "tree:select");
      await RpcClient.call("subscribe", "client:dumped");
      await RpcClient.call("subscribe", "showRelogForm");
      subscribe("showRelogForm", (params) => {
        openModal(params, "relogin");
      });

      await RpcClient.call("subscribe", "shouldCallGetTree");
      subscribe("shouldCallGetTree", (params) => {
        dispatch(ResursTreeApi.getTree(params.tree)({ objectId: ["root"] }));
      });

      await RpcClient.call("subscribe", "nav:redirect");
      subscribe("nav:redirect", handleNavRedirect);

      await RpcClient.call("subscribe", "dataTable:read");
      subscribe("dataTable:read", (params) => {
        switch (params.tableName) {
          case "massKDLConsumption": {
            openModal(params, "massKDLConsumption");
            break;
          }
          case "power_profile": {
            openModal(params, "power_profile");
            break;
          }

          case "counter_data": {
            openModal(params, "counter_data");
            break;
          }

          default:
            return;
        }
      });

      await RpcClient.call("subscribe", "tree:upd");
      subscribe("tree:upd", handleTreeUpd);

      await RpcClient.call("subscribe", "setFindResult");
      subscribe("setFindResult", (params) => {
        openModal(params, "find-dialog");
        setModalData(params);
      });

      await RpcClient.call("subscribe", "setDiagnostic");
      subscribe("setDiagnostic", (params) => {
        openModal(params, "diagnostic-dialog");
      });

      await RpcClient.call("subscribe", "createChoiceList");
      subscribe("createChoiceList", (params) => {
        openModal(params, "create-choice-list");
      });

      await RpcClient.call("subscribe", "createDialog");
      subscribe("createDialog", (params) => {
        openModal(params, "create-dialog");
      });

      await RpcClient.call("subscribe", "showNotification");
      subscribe("showNotification", async (params) => {
        if (params.tag === "planner") {
          updatePlannerReady();
          showMessage(params);
        } else {
          showMessage(params);
        }
      });
    })();

    return () => {
      stopFlushInterval();
    };
  }, [wsOpened, isAuthenticated, stopFlushInterval]);

  return (
    <>
      {children}
      <BlockUi />
      {modalData && modalId === "create-dialog" && isAuthenticated && (
        <CreateDialogModal
          show={showModal}
          close={closeModal}
          params={modalData}
        />
      )}

      {modalData && modalId === "find-dialog" && isAuthenticated && (
        <FindDialogModal show={showModal} params={modalData} />
      )}

      {modalData && modalId === "diagnostic-dialog" && isAuthenticated && (
        <DiagnosticDialogModal
          show={showModal}
          close={closeModal}
          params={modalData}
        />
      )}

      {modalData && modalId === "create-choice-list" && isAuthenticated && (
        <CreateChoiceListDialog
          show={showModal}
          close={closeModal}
          params={modalData}
        />
      )}
      {modalData && modalId === "command-parameter" && (
        <CommandParameterDialog
          show={showModal}
          close={closeModal}
          params={modalData}
        />
      )}

      {modalData && modalId === "massKDLConsumption" && isAuthenticated && (
        <MassKDLDialog show={showModal} params={modalData} close={closeModal} />
      )}

      {modalData && modalId === "power_profile" && isAuthenticated && (
        <PowerProfileDialog
          show={showModal}
          dataTableParams={modalData}
          close={closeModal}
        />
      )}

      {modalData && modalId === "counter_data" && isAuthenticated && (
        <CounterDataDialog
          show={showModal}
          dataTableParams={modalData}
          close={closeModal}
        />
      )}

      {modalData && modalId === "relogin" && isAuthenticated && (
        <ReloginForm
          show={showModal}
          close={closeModal}
          reloginType="behaviour_change"
        />
      )}
    </>
  );
};

export default memo(SubscribeContextProvider);
